import { useCustom } from "@refinedev/core";
import type { LifecycleInfo } from "../types";

interface UseLifecycleMapOptions {
  mapId: string | undefined;
  enabled?: boolean;
}

interface UseLifecycleMapResult {
  lifecycleMap: LifecycleInfo | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to fetch a Life Cycle Map by its ID.
 * Returns all states and transitions for the lifecycle map.
 * Used by Kanban board to determine column order and valid transitions.
 *
 * @param mapId - The lifecycle map GUID from ItemTypeMetadata.lifecycleMapId
 * @param enabled - Whether to enable the fetch (default: true)
 */
export const useLifecycleMap = ({
  mapId,
  enabled = true,
}: UseLifecycleMapOptions): UseLifecycleMapResult => {
  const shouldFetch = enabled && !!mapId;

  const { data, isLoading, isError } = useCustom<LifecycleInfo>({
    url: `/meta/lifecycle-map/${mapId}`,
    method: "get",
    queryOptions: {
      enabled: shouldFetch,
      staleTime: 10 * 60 * 1000, // 10 minutes — lifecycle maps rarely change
      retry: 1,
    },
  });

  return {
    lifecycleMap: data?.data || null,
    isLoading: shouldFetch && isLoading,
    isError,
  };
};
